import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight } from "lucide-react";

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/featured");
        setFeatured(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — asymmetric split */}
      <section className="relative overflow-hidden -mt-20 md:-mt-24 pt-20 md:pt-24 bg-sand">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-ink/5 blur-3xl" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[80vh]">
          {/* Text */}
          <div className="text-ink animate-fade-up opacity-0" style={{ animationDelay: "120ms" }}>
            <div className="eyebrow mb-6">Niche Perfumery · est. 2026</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Fragrance, <em className="italic font-light text-gold">curated</em>
              <br />
              with intention.
            </h1>
            <p className="mt-8 text-base md:text-lg text-graphite font-light max-w-lg leading-relaxed">
              A quiet rebellion against the ordinary. Discover rare impressions
              of iconic scents — delivered to your door, ordered over WhatsApp.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-3 bg-ink text-cream px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-gold transition-all duration-500"
              >
                Explore Collection
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/shop/women"
                className="inline-flex items-center text-xs uppercase tracking-[0.22em] font-medium text-ink border-b border-ink/30 hover:border-gold hover:text-gold pb-1 transition"
              >
                For Her
              </Link>
            </div>
          </div>

          {/* Featured Bottle */}
          <div
            className="relative animate-fade-up opacity-0"
            style={{ animationDelay: "260ms" }}
          >
            <div className="absolute -inset-6 md:-inset-10 bg-gradient-to-br from-gold/20 via-transparent to-ink/5 blur-2xl" />
            <div className="relative aspect-square w-full max-w-[560px] mx-auto">
              <div className="absolute inset-0 bg-cream shadow-[0_30px_80px_-20px_rgba(26,26,26,0.25)]" />
              <img
                src="/images/perfume-8.jpg"
                alt="MZ scents — our impression of Victoria Secret Tease"
                className="relative w-full h-full object-cover"
                loading="eager"
              />
              {/* Floating tag */}
              <div className="absolute -bottom-5 -left-3 md:-left-6 bg-ink text-cream px-5 py-4 max-w-[240px] shadow-xl">
                <div className="eyebrow text-gold mb-1 text-[9px]">New Arrival</div>
                <div className="font-serif text-xl leading-tight">Victoria Secret Tease</div>
                <div className="text-[11px] text-cream/70 mt-1 tracking-wide">MZ scents impression</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES TRIPTYCH */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="eyebrow mb-3">Three Worlds</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink tracking-tight">
              Find your <em className="italic">signature</em>.
            </h2>
          </div>
          <Link
            to="/shop"
            className="hidden md:inline-flex link-underline text-xs uppercase tracking-[0.22em] text-ink hover:text-gold"
          >
            View all
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            {
              to: "/shop/women",
              label: "For Her",
              img: "/images/perfume-3.jpg",
              desc: "Florals, iris, suede.",
            },
            {
              to: "/shop/men",
              label: "For Him",
              img: "/images/perfume-1.jpg",
              desc: "Leather, cedar, smoke.",
            },
            {
              to: "/shop/unisex",
              label: "Unisex",
              img: "/images/perfume-5.jpg",
              desc: "Oud, amber, tobacco.",
            },
          ].map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`category-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative aspect-[4/5] overflow-hidden bg-sand animate-fade-up opacity-0"
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <img src={c.img} alt={c.label} className="img-zoom w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7 text-cream">
                <div className="eyebrow text-gold mb-2">{c.desc}</div>
                <div className="font-serif text-4xl">{c.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="eyebrow mb-3">Editor's Selection</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">
              Featured fragrances
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex link-underline text-xs uppercase tracking-[0.22em] text-ink hover:text-gold">
            All perfumes
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-sand animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {featured.slice(0, 8).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ABOUT BLOCK */}
      <section className="bg-sand py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="eyebrow mb-4">The MZ Philosophy</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight">
              Scent is the most <em className="italic">intimate</em> language.
            </h2>
            <p className="mt-7 text-base text-graphite leading-relaxed font-light max-w-md">
              Every bottle we stock is hand-picked by our founder — tested,
              worn, and lived in. We believe in slow rituals, considered
              choices, and fragrances that tell quiet stories.
            </p>
            <Link
              to="/shop"
              className="mt-8 inline-block link-underline text-xs uppercase tracking-[0.22em] text-ink hover:text-gold"
            >
              Browse the collection
            </Link>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="/images/perfume-7.jpg"
              alt="MZ philosophy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

